import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Loader2, Route as RouteIcon, CheckCircle2 } from 'lucide-react';
import {
  brokerSelectionService as svc,
  type BrokerSelection,
  type BrokerClassSelection,
} from '@/services/brokerSelectionService';

// Provider code -> friendly label.
function label(code: string | null | undefined): string {
  switch (code) {
    case 'Alpaca': return 'Alpaca';
    case 'Fxcm': return 'FXCM';
    case 'Ig': return 'IG Markets';
    default: return code || '—';
  }
}

/**
 * Configuration → Broker Routing.
 * Picks which registered broker serves each asset class when more than one does
 * (Equity: Alpaca | IG; Forex: FXCM | IG). The choice is honoured everywhere the
 * resolve passes no explicit provider — automation, the AI tools, manual panels.
 */
export const BrokerRoutingSettingsPage: React.FC = () => {
  const [sel, setSel] = useState<BrokerSelection | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setSel(await svc.get());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load broker routing');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-3 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">Broker Routing</h2>
        <p className="text-xs text-muted-foreground">
          Choose which broker handles each asset class. Applies to automation, the AI tools, and manual orders.
        </p>
      </div>

      {error && (
        <div className="text-xs text-red-500 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {error}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <RouteIcon className="h-4 w-4 text-primary" />
            Broker per asset class
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {!loaded ? (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : sel ? (
            <>
              <ClassRow cls={sel.equity} onChanged={(s) => setSel(s)} />
              <ClassRow cls={sel.forex} onChanged={(s) => setSel(s)} />
              {/* Rendered only when the server actually offers crypto routing, so an
                  older API that returns two classes does not produce an empty row. */}
              {sel.crypto && <ClassRow cls={sel.crypto} onChanged={(s) => setSel(s)} />}
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

const ClassRow: React.FC<{
  cls: BrokerClassSelection;
  onChanged: (s: BrokerSelection) => void;
}> = ({ cls, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const value = cls.selected ?? '';   // '' = default

  const onSelect = async (next: string) => {
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const updated = await svc.set(cls.assetClass, next === '' ? null : next);
      onChanged(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e: any) {
      setErr(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold">{cls.assetClass}</div>
        <div className="text-[11px] text-muted-foreground">
          {cls.selected ? `Using ${label(cls.selected)}` : `Using default (${label(cls.default)})`}
        </div>
        {err && <div className="text-[11px] text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {err}</div>}
      </div>
      <div className="flex items-center gap-2">
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        {saved && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
        <select
          value={value}
          disabled={saving}
          onChange={(e) => void onSelect(e.target.value)}
          className="h-8 rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">Default ({label(cls.default)})</option>
          {cls.options.map((o) => (
            <option key={o} value={o}>{label(o)}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default BrokerRoutingSettingsPage;
